import React, { useEffect, useState } from 'react';
import { Arzt, Praxis, Zeitslot, AppType, ZuweisungFilter as FilterType } from '../../types';
import { arztService } from '../../services/arzt.service';
import { praxisService } from '../../services/praxis.service';
import { scheduleService } from '../../services/schedule.service';
import { useEnvironment } from '../../context/EnvironmentContext';

interface DefaultScheduleViewProps {
    refreshTrigger?: number;
    filter?: FilterType;
}

const DefaultScheduleView: React.FC<DefaultScheduleViewProps> = ({ refreshTrigger, filter }) => {
    const { currentApp } = useEnvironment();
    const [praxen, setPraxen] = useState<Praxis[]>([]);
    const [aerzte, setAerzte] = useState<Arzt[]>([]);
    const [defaults, setDefaults] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingCell, setEditingCell] = useState<string | null>(null);

    const weekDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    const dayLabels: Record<string, string> = {
        MONDAY: 'Montag',
        TUESDAY: 'Dienstag',
        WEDNESDAY: 'Mittwoch',
        THURSDAY: 'Donnerstag',
        FRIDAY: 'Freitag',
        SATURDAY: 'Samstag'
    };

    const hasAfternoonSlot = (day: string) => {
        return !['WEDNESDAY', 'FRIDAY', 'SATURDAY'].includes(day);
    };

    const fetchData = React.useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const [praxenData, aerzteData, defaultsData] = await Promise.all([
                praxisService.getAllPraxen(currentApp as AppType),
                arztService.getAllAerzte(currentApp as AppType),
                scheduleService.getDefaultSchedules(currentApp as AppType)
            ]);

            setPraxen(praxenData);
            setAerzte(aerzteData);
            setDefaults(defaultsData);
        } catch (err: any) {
            setError(err.message || 'Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    }, [currentApp]);

    useEffect(() => {
        fetchData();
    }, [fetchData, refreshTrigger]);

    const getAssignmentsForCell = (praxisId: number, day: string, zeitslot: Zeitslot) => {
        let results = defaults.filter(d =>
            d.praxisId === praxisId &&
            d.dayOfWeek === day &&
            d.zeitslot === zeitslot
        );

        if (filter?.arztId) {
            results = results.filter(d => d.arztId === filter.arztId);
        }

        return results.sort((a, b) => {
            const nameA = a.arzt ? `${a.arzt.nachname} ${a.arzt.vorname}` : '';
            const nameB = b.arzt ? `${b.arzt.nachname} ${b.arzt.vorname}` : '';
            return nameA.localeCompare(nameB);
        });
    };

    const sortedPraxen = [...praxen].sort((a, b) => a.name.localeCompare(b.name));

    const filteredPraxen = sortedPraxen.filter(p => {
        if (filter?.praxisId && p.id !== filter.praxisId) return false;
        // If filtering by Arzt, we might want to hide praxen where the Arzt has NO assignments?
        // For now, let's just filter Praxen if praxisId is set.
        return true;
    });

    const handleCellClick = (praxisId: number, day: string, zeitslot: Zeitslot) => {
        setEditingCell(`${praxisId}-${day}-${zeitslot}`);
    };

    const handleArztAdd = async (praxisId: number, day: string, zeitslot: Zeitslot, arztId: string) => {
        if (!arztId) return;
        try {
            await scheduleService.createDefaultSchedule({
                arztId: parseInt(arztId),
                praxisId,
                dayOfWeek: day,
                zeitslot,
                app: currentApp as AppType
            });
            fetchData();
            setEditingCell(null);
        } catch (err: any) {
            alert('Fehler beim Speichern: ' + err.message);
        }
    };

    const handleArztRemove = async (id: number) => {
        try {
            await scheduleService.deleteDefaultSchedule(id);
            fetchData();
        } catch (err: any) {
            alert('Fehler beim Löschen: ' + err.message);
        }
    };

    const getAvailableAerzte = (praxisId: number, day: string, zeitslot: Zeitslot) => {
        const dayNameGerman = dayLabels[day];

        return aerzte.filter(arzt => {
            // 1. Check Availability (Praxis Associations)
            // If the doctor has associations, they MUST include this praxis and this day.
            // If no associations defined at all? Use fallback or allow? Check user requirement.
            // "nur die Ärtze bei denen... eingestellt ist" -> imply strict check.

            // Note: arzt.praxisAssociations might be undefined if not fetched with include.
            // arztService.getAllAerzte() includes it.

            if (arzt.praxisAssociations && arzt.praxisAssociations.length > 0) {
                const association = arzt.praxisAssociations.find(a => a.praxisId === praxisId);
                if (!association) return false; // Not associated with this praxis at all
                if (!association.arbeitstage.includes(dayNameGerman)) return false; // Not working on this day
            }
            // If praxisAssociations is empty/undefined, maybe allow? 
            // But usually we want strictness if the feature is used.
            // Given the requirement "nur ...", I assume strict check if associations exist.
            // If newly created doctor has no associations, they won't appear. That is correct.

            // 2. Check Conflicts (Already assigned anywhere for this slot - same or different praxis)
            const isAlreadyAssigned = defaults.some(d =>
                d.arztId === arzt.id &&
                d.dayOfWeek === day &&
                d.zeitslot === zeitslot
            );

            if (isAlreadyAssigned) return false;

            return true;
        });
    };

    if (loading) return <div>Lädt...</div>;
    if (error) return <div className="text-red-600">{error}</div>;

    return (
        <div className="bg-white shadow-md rounded-lg overflow-x-auto">
            <div className="p-4 bg-yellow-50 border-b border-yellow-100 mb-4">
                <h2 className="text-lg font-bold text-yellow-800">Feste Zuordnung ({currentApp})</h2>
                <p className="text-sm text-yellow-700">Definieren Sie hier das wöchentliche Standard-Schema.</p>
            </div>

            <table className="min-w-full border-collapse">
                <thead>
                    <tr className="bg-gray-50">
                        <th className="border p-2 text-left sticky left-0 bg-gray-50 z-10">Praxis</th>
                        {weekDays.map(day => (
                            <th key={day} colSpan={hasAfternoonSlot(day) ? 2 : 1} className="border p-2 text-center">
                                {dayLabels[day]}
                            </th>
                        ))}
                    </tr>
                    <tr className="bg-gray-100">
                        <th className="border p-2 sticky left-0 bg-gray-100"></th>
                        {weekDays.map(day => (
                            <React.Fragment key={`${day}-slots`}>
                                <th className="border p-1 text-xs text-blue-700">VM</th>
                                {hasAfternoonSlot(day) && <th className="border p-1 text-xs text-purple-700">NM</th>}
                            </React.Fragment>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {filteredPraxen.map(praxis => (
                        <tr key={praxis.id} className="hover:bg-gray-50">
                            <td className="border p-2 font-medium sticky left-0 bg-white z-10">{praxis.name}</td>
                            {weekDays.map(day => (
                                <React.Fragment key={`${praxis.id}-${day}`}>
                                    <td className="border bg-blue-50 p-1 align-top min-w-[120px]">
                                        {/* VM Cell */}
                                        <div className="flex flex-col gap-1">
                                            {getAssignmentsForCell(praxis.id, day, Zeitslot.VORMITTAG).map(a => (
                                                <div key={a.id} className="flex justify-between items-center bg-white border rounded px-1 text-xs">
                                                    <span>{a.arzt.vorname} {a.arzt.nachname}</span>
                                                    <button onClick={() => handleArztRemove(a.id)} className="text-red-500 hover:text-red-700 ml-1">×</button>
                                                </div>
                                            ))}
                                            {editingCell === `${praxis.id}-${day}-${Zeitslot.VORMITTAG}` ? (
                                                <select
                                                    autoFocus
                                                    className="text-xs w-full"
                                                    onChange={(e) => handleArztAdd(praxis.id, day, Zeitslot.VORMITTAG, e.target.value)}
                                                    onBlur={() => setEditingCell(null)}
                                                >
                                                    <option value="">Wählen...</option>
                                                    {getAvailableAerzte(praxis.id, day, Zeitslot.VORMITTAG).map(arzt => (
                                                        <option key={arzt.id} value={arzt.id}>{arzt.vorname} {arzt.nachname}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <button onClick={() => handleCellClick(praxis.id, day, Zeitslot.VORMITTAG)} className="text-blue-500 text-xs text-left hover:underline">+ Add</button>
                                            )}
                                        </div>
                                    </td>
                                    {hasAfternoonSlot(day) && (
                                        <td className="border bg-purple-50 p-1 align-top min-w-[120px]">
                                            {/* NM Cell */}
                                            <div className="flex flex-col gap-1">
                                                {getAssignmentsForCell(praxis.id, day, Zeitslot.NACHMITTAG).map(a => (
                                                    <div key={a.id} className="flex justify-between items-center bg-white border rounded px-1 text-xs">
                                                        <span>{a.arzt.vorname} {a.arzt.nachname}</span>
                                                        <button onClick={() => handleArztRemove(a.id)} className="text-red-500 hover:text-red-700 ml-1">×</button>
                                                    </div>
                                                ))}
                                                {editingCell === `${praxis.id}-${day}-${Zeitslot.NACHMITTAG}` ? (
                                                    <select
                                                        autoFocus
                                                        className="text-xs w-full"
                                                        onChange={(e) => handleArztAdd(praxis.id, day, Zeitslot.NACHMITTAG, e.target.value)}
                                                        onBlur={() => setEditingCell(null)}
                                                    >
                                                        <option value="">Wählen...</option>
                                                        {getAvailableAerzte(praxis.id, day, Zeitslot.NACHMITTAG).map(arzt => (
                                                            <option key={arzt.id} value={arzt.id}>{arzt.vorname} {arzt.nachname}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <button onClick={() => handleCellClick(praxis.id, day, Zeitslot.NACHMITTAG)} className="text-blue-500 text-xs text-left hover:underline">+ Add</button>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </React.Fragment>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default DefaultScheduleView;
